import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertCircle, Loader2, Plus, Trophy, ChevronRight } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@sportscanner/ui";
import { createCompetitionSchema, hasFeature } from "@sportscanner/schemas";
import { apiGet, apiPost, apiPut, getApiErrorCode } from "@/lib/api";
import ClubScopeShell from "@/components/club-scope-shell";
import CompetitionDetail from "./detail";
import {
  COMPETITION_FORMATS,
  type CompetitionFormat,
  type CompetitionListResponse,
} from "./types";

export default function CompetitionsPage() {
  const { t } = useTranslation();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("competitions.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("competitions.subtitle")}</p>
      </div>
      <ClubScopeShell>
        {(clubId) => <CompetitionsManager clubId={clubId} />}
      </ClubScopeShell>
    </div>
  );
}

function CompetitionsManager({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["admin", "competitions", clubId],
    queryFn: () =>
      apiGet<CompetitionListResponse>(
        `/api/admin/clubs/${clubId}/competitions`,
      ),
  });

  const toggleMutation = useMutation({
    mutationFn: (competitionsEnabled: boolean) =>
      apiPut(`/api/admin/clubs/${clubId}/competitions-enabled`, {
        competitionsEnabled,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "competitions", clubId],
      });
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  const { competitionsEnabled, subscriptionTier, sports, competitions } =
    query.data;

  if (selectedId) {
    return (
      <CompetitionDetail
        clubId={clubId}
        competitionId={selectedId}
        sports={sports}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  const tierOk = hasFeature(subscriptionTier, "competitionsEnabled");

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <section className="bg-white rounded-xl border border-neutral-200 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-medium text-neutral-800">
              {t("competitions.enableLabel")}
              {!tierOk && (
                <Badge variant="secondary" className="ml-2">
                  PRO
                </Badge>
              )}
            </Label>
            <p className="text-xs text-neutral-500 mt-0.5">
              {t("competitions.enableHint")}
            </p>
          </div>
          <Switch
            checked={competitionsEnabled}
            disabled={
              toggleMutation.isPending || (!tierOk && !competitionsEnabled)
            }
            onCheckedChange={(v) => toggleMutation.mutate(v)}
          />
        </div>
      </section>

      {/* List header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold text-neutral-800">
          {t("competitions.listTitle")}
        </h2>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          disabled={sports.length === 0}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {t("competitions.add")}
        </Button>
      </div>

      {sports.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center">
          <p className="text-sm text-neutral-500">
            {t("competitions.noSports")}
          </p>
        </div>
      ) : competitions.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
            <Trophy className="w-6 h-6 text-neutral-400" />
          </div>
          <p className="text-sm text-neutral-500">{t("competitions.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {competitions.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className="w-full text-left bg-white rounded-xl border border-neutral-200 p-4 flex items-center gap-4 hover:border-primary-300 transition-colors"
            >
              <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-primary-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-neutral-800 truncate">
                    {c.name}
                  </p>
                  <Badge variant="secondary">
                    {t(`competitions.status.${c.status}`)}
                  </Badge>
                </div>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {t(`competitions.format.${c.format}`)} ·{" "}
                  {t(`sports.${c.sport}`, { defaultValue: c.sport })}
                  {c.season ? ` · ${c.season}` : ""} ·{" "}
                  {t("competitions.entrants", { count: c.participantCount })}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {creating && (
        <CompetitionCreateDialog
          clubId={clubId}
          sports={sports}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
}

function CompetitionCreateDialog({
  clubId,
  sports,
  onClose,
  onCreated,
}: {
  clubId: string;
  sports: { id: string; name: string }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [season, setSeason] = useState("");
  const [sportId, setSportId] = useState(sports[0]?.id ?? "");
  const [format, setFormat] = useState<CompetitionFormat>("GROUPS_KNOCKOUT");
  const [thirdPlace, setThirdPlace] = useState(false);
  const [divisions, setDivisions] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        sportId,
        name: name.trim(),
        format,
        season: season.trim() || null,
        thirdPlaceMatch: thirdPlace,
        divisions: divisions
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean),
      };
      const parsed = createCompetitionSchema.safeParse(payload);
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      return apiPost<{ id: string }>(
        `/api/admin/clubs/${clubId}/competitions`,
        parsed.data,
      );
    },
    onSuccess: (res) => {
      toast.success(t("common.saved"));
      onCreated(res.id);
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("competitions.addTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="comp-name" className="text-sm text-neutral-600">
              {t("competitions.nameLabel")}
            </Label>
            <Input
              id="comp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("competitions.namePlaceholder")}
              className="rounded-lg border-neutral-200"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm text-neutral-600">
                {t("competitions.sportLabel")}
              </Label>
              <Select value={sportId} onValueChange={setSportId}>
                <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {t(`sports.${s.name}`, { defaultValue: s.name })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="comp-season" className="text-sm text-neutral-600">
                {t("competitions.seasonLabel")}
              </Label>
              <Input
                id="comp-season"
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                placeholder={t("competitions.seasonPlaceholder")}
                className="rounded-lg border-neutral-200"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-600">
              {t("competitions.formatLabel")}
            </Label>
            <Select
              value={format}
              onValueChange={(v) => setFormat(v as CompetitionFormat)}
            >
              <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPETITION_FORMATS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {t(`competitions.format.${f}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="comp-div" className="text-sm text-neutral-600">
              {t("competitions.divisionsLabel")}
            </Label>
            <Input
              id="comp-div"
              value={divisions}
              onChange={(e) => setDivisions(e.target.value)}
              placeholder={t("competitions.divisionsPlaceholder")}
              className="rounded-lg border-neutral-200"
            />
            <p className="text-xs text-neutral-400">
              {t("competitions.divisionsHint")}
            </p>
          </div>
          {format !== "LEAGUE" && (
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm font-medium text-neutral-800">
                {t("competitions.thirdPlaceLabel")}
              </Label>
              <Switch checked={thirdPlace} onCheckedChange={setThirdPlace} />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.create")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
